<script setup>
  import { useRoute } from 'vue-router'

  import { computed, ref, watch } from 'vue'

  import MainLayout from '@/features/layout/MainLayout.vue'
  import { useAdminSidebarMenuLinks } from '@/features/layout/components/constants/useAdminSidebarMenuLinks.js'
  import { useHeaderRightActions } from '@/features/layout/components/utils/useHeaderRightActions.js'
  import { useImpersonationLocation } from '@/features/layout/components/utils/useImpersonationLocation.js'
  import { decidePermission } from '@/features/permissions/permissionEngine.js'
  import WelcomeToClubModal from '@/features/layout/welcomeToClubModal/WelcomeToClubModal.vue'
  import {
    finishClubImpersonation,
    getActiveClubDisplayName,
    useActiveClub,
  } from '@/features/router/useActiveClub.js'
  import { SuperAdminLimitedRootViewActions } from '@/features/superAdmin/superAdminLimitedPermissionActions.js'

  import { useAuthStore } from '../auth/useAuthStore'
  import { getIsImpersonation } from '../router/useActiveClub'
  import { useSuperAdminIdentity } from '../superAdmin/useIsSuperAdmin'
  import { useToaster } from '../ui/toaster/useToaster'

  const { activeClub } = useActiveClub()

  const authStore = useAuthStore()
  const { isFullSuperAdmin, isSuperAdminLimited } = useSuperAdminIdentity()

  const toaster = useToaster()

  const impersonationLocation = useImpersonationLocation()
  function handleClickSuperAdmin() {
    if (getIsImpersonation(authStore.myDetails)) {
      finishClubImpersonation(null)

      toaster.success({
        title: 'Impersonation stopped',
        detail: 'You stopped impersonating a club',
      })

      impersonationLocation.reset()
      window.location.href = '/super-admin'
    } else {
      impersonationLocation.reset()
      window.location.href = '/super-admin'
      toaster.success({
        title: 'Redirecting to super admin',
      })
    }
  }

  const canOpenSuperAdmin = computed(() => {
    if (isFullSuperAdmin.value) return true
    if (!isSuperAdminLimited.value) return false

    return SuperAdminLimitedRootViewActions.some(
      action =>
        decidePermission(action, {
          myDetails: authStore.myDetails,
          surface: 'super-admin',
          route: '/super-admin',
        }).allowed,
    )
  })

  useHeaderRightActions(
    computed(() => [
      canOpenSuperAdmin.value
        ? {
            label: 'common.superAdmin',
            icon: 'pi pi-cog',
            onClick: handleClickSuperAdmin,
            attrs: {
              class: 'header-right-super-admin-button',
            },
          }
        : null,
    ]),
  )

  const menuLinks = useAdminSidebarMenuLinks()

  const route = useRoute()

  function setShowSidebar(showSidebarParam) {
    return showSidebarParam === undefined ? true : showSidebarParam
  }

  const showSidebarMetaValue = ref(setShowSidebar(route.meta.showSidebar))

  watch(
    () => route.meta.showSidebar,
    value => {
      showSidebarMetaValue.value = setShowSidebar(value)
    },
  )

  const hamburgerTitle = computed(() => getActiveClubDisplayName(activeClub))
</script>

<template>
  <WelcomeToClubModal />

  <MainLayout
    :hamburger-title="hamburgerTitle"
    :sidebar-links="menuLinks"
    :show-sidebar="showSidebarMetaValue"
  />
</template>

<style lang="scss" scoped></style>
